import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { CaseStudy } from '@/constants/case-studies'

import Image from 'next/image'
import { ArrowLeft, ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Counter } from '@/components/generals/counter'
import { Container } from '@/components/generals/container'
import { RollText } from '@/components/generals/roll-text'
import { CtaSection } from '@/components/generals/cta-section'
import { SectionLabel } from '@/components/generals/section-label'
import { TransitionLink } from '@/components/generals/transition-link'
import { SystemDiagram } from '@/components/landing/case-studies/system-diagram'

import { CASE_STUDIES, getCaseStudy } from '@/constants/case-studies'

interface CaseStudyPageProps {
    study: CaseStudy
    next: Pick<CaseStudy, 'slug' | 'client' | 'industry' | 'title' | 'image' | 'imageAlt' | 'imageClassName'>
}

const CaseStudyPage: FC<CaseStudyPageProps> = ({ study, next }): ReactNode => {
    const initials = study.quote.name
        .replace('Dr. ', '')
        .split(' ')
        .map((part) => part[0])
        .join('')
        .slice(0, 2)

    return (
        <Layout>
            <Seo
                title={study.client}
                description={study.summary}
            />

            {/* Hero */}
            <section id='case-study-hero' aria-labelledby='case-study-title' className='relative pt-32 md:pt-44'>
                <Container>
                    <div data-reveal='fade' className='flex flex-wrap items-center justify-between gap-4'>
                        <TransitionLink
                            href='/case-studies'
                            className='group inline-flex items-center gap-2 text-sm text-soft transition-colors hover:text-foreground'
                        >
                            <ArrowLeft className='size-4 transition-transform duration-500 ease-expo group-hover:-translate-x-1' />
                            <RollText>All case studies</RollText>
                        </TransitionLink>

                        <SectionLabel>{`${study.industry} / ${study.client}`}</SectionLabel>
                    </div>

                    <h1 id='case-study-title' data-split className='type-h1 mt-12 max-w-[20ch]'>
                        {study.title}
                    </h1>

                    <div className='mt-12 grid gap-10 lg:grid-cols-12 lg:gap-6'>
                        <p data-reveal data-delay='0.2' className='type-lead text-soft lg:col-span-6'>
                            {study.summary}
                        </p>

                        <dl data-reveal-group='0.06' className='grid grid-cols-2 gap-x-6 border-t border-line lg:col-span-5 lg:col-start-8'>
                            {[
                                { term: 'Client', detail: study.client },
                                { term: 'Industry', detail: study.industry },
                                { term: 'Duration', detail: study.duration },
                                { term: 'Services', detail: study.services.join(', ') }
                            ].map((item) => (
                                <div key={item.term} className='flex flex-col gap-1.5 border-b border-line py-4'>
                                    <dt className='type-label text-muted-foreground'>
                                        {item.term}
                                    </dt>

                                    <dd className='text-[0.9375rem] text-foreground'>
                                        {item.detail}
                                    </dd>
                                </div>
                            ))}
                        </dl>
                    </div>
                </Container>

                <Container className='mt-16 md:mt-20'>
                    <div data-reveal='image' className='relative aspect-[4/3] overflow-hidden md:aspect-[21/9]'>
                        <div data-parallax='8' className='absolute -inset-y-[12%] inset-x-0'>
                            <Image
                                src={study.image}
                                alt={study.imageAlt}
                                fill
                                preload
                                sizes='(min-width: 1440px) 1360px, 100vw'
                                className={cn('img-tone object-cover', study.imageClassName)}
                            />
                        </div>
                    </div>
                </Container>
            </section>

            {/* Key metrics */}
            <section id='key-metrics' aria-label='Key results' className='relative py-16 md:py-24'>
                <Container>
                    <dl data-reveal-group='0.08' className='grid grid-cols-2 gap-y-10 lg:grid-cols-4'>
                        {study.metrics.map((metric) => (
                            <div key={metric.label} className='flex flex-col-reverse justify-end gap-3 border-l border-line pl-5 md:pl-6'>
                                <dt className='max-w-[22ch] text-sm text-soft'>
                                    {metric.label}
                                </dt>

                                <dd className='font-heading text-[clamp(2.4rem,5vw,4.5rem)] leading-none tracking-[-0.05em] text-foreground'>
                                    <Counter
                                        value={metric.value}
                                        decimals={metric.decimals}
                                        prefix={metric.prefix}
                                        suffix={metric.suffix}
                                    />
                                </dd>
                            </div>
                        ))}
                    </dl>
                </Container>
            </section>

            {/* Challenge */}
            <section id='challenge' aria-labelledby='challenge-title' className='relative border-t border-line py-24 md:py-32'>
                <Container className='grid gap-10 lg:grid-cols-12 lg:gap-6'>
                    <div className='lg:col-span-4'>
                        <SectionLabel index='01'>The challenge</SectionLabel>

                        <h2 id='challenge-title' data-split className='type-h2 mt-8 max-w-[10ch]'>
                            Where the work was stuck.
                        </h2>
                    </div>

                    <div className='flex flex-col gap-12 lg:col-span-7 lg:col-start-6'>
                        <div data-reveal className='type-lead flex flex-col gap-6 text-soft'>
                            {study.challenge.map((paragraph) => (
                                <p key={paragraph.slice(0, 24)}>{paragraph}</p>
                            ))}
                        </div>

                        <dl data-reveal-group='0.08' className='grid gap-px border border-line bg-line sm:grid-cols-3'>
                            {study.painPoints.map((point) => (
                                <div key={point.label} className='flex flex-col-reverse justify-end gap-2 bg-background p-5 md:p-6'>
                                    <dt className='text-sm text-muted-foreground'>
                                        {point.label}
                                    </dt>

                                    <dd className='font-heading text-[2rem] leading-none tracking-[-0.04em] text-foreground/80'>
                                        {point.value}
                                    </dd>
                                </div>
                            ))}
                        </dl>
                    </div>
                </Container>
            </section>

            {/* Solution */}
            <section id='solution' aria-labelledby='solution-title' className='relative border-t border-line py-24 md:py-32'>
                <Container className='grid gap-10 lg:grid-cols-12 lg:gap-6'>
                    <div className='lg:col-span-4'>
                        <SectionLabel index='02'>The solution</SectionLabel>

                        <h2 id='solution-title' data-split className='type-h2 mt-8 max-w-[11ch]'>
                            What Cortex built.
                        </h2>
                    </div>

                    <div data-reveal className='type-lead flex flex-col gap-6 text-soft lg:col-span-7 lg:col-start-6'>
                        {study.solution.map((paragraph) => (
                            <p key={paragraph.slice(0, 24)}>{paragraph}</p>
                        ))}
                    </div>
                </Container>
            </section>

            {/* Automation system */}
            <section id='automation-system' aria-labelledby='system-title' className='relative border-t border-line py-24 md:py-32'>
                <Container>
                    <div className='grid gap-8 lg:grid-cols-12 lg:gap-6'>
                        <div className='lg:col-span-4'>
                            <SectionLabel index='03'>Automation system</SectionLabel>
                        </div>

                        <h2 id='system-title' data-split className='type-h2 max-w-[16ch] lg:col-span-8'>
                            How the system works, step by step.
                        </h2>
                    </div>

                    <div className='mt-14 md:mt-20'>
                        <SystemDiagram nodes={study.system} />
                    </div>
                </Container>
            </section>

            {/* Results */}
            <section id='results' aria-labelledby='results-title' className='relative border-t border-line py-24 md:py-32'>
                <Container className='grid gap-12 lg:grid-cols-12 lg:gap-6'>
                    <div className='lg:col-span-4'>
                        <SectionLabel index='04'>Results</SectionLabel>
                    </div>

                    <div className='flex flex-col gap-16 lg:col-span-8'>
                        <p id='results-title' data-words className='type-statement text-foreground'>
                            {study.results}
                        </p>

                        <figure data-reveal className='grid gap-8 border-t border-line pt-10 md:grid-cols-12'>
                            <span aria-hidden='true' className='serif-accent text-[5rem] leading-[0.6] text-ember md:col-span-1'>
                                “
                            </span>

                            <div className='flex flex-col gap-8 md:col-span-11'>
                                <blockquote className='font-heading text-[clamp(1.35rem,2vw,1.8rem)] leading-[1.3] tracking-[-0.02em] text-foreground'>
                                    {study.quote.text}
                                </blockquote>

                                <figcaption className='flex items-center gap-4'>
                                    <span className='flex size-12 items-center justify-center border border-line-strong font-heading text-sm text-foreground'>
                                        {initials}
                                    </span>

                                    <span>
                                        <span className='block text-foreground'>
                                            {study.quote.name}
                                        </span>

                                        <span className='block text-sm text-muted-foreground'>
                                            {study.quote.role}
                                        </span>
                                    </span>
                                </figcaption>
                            </div>
                        </figure>
                    </div>
                </Container>
            </section>

            {/* Next project */}
            <section id='next-project' aria-label='Next case study' className='relative border-t border-line'>
                <TransitionLink href={`/case-studies/${next.slug}`} className='group relative block overflow-hidden'>
                    <div className='absolute inset-0'>
                        <Image
                            src={next.image}
                            alt=''
                            fill
                            sizes='100vw'
                            className={cn('img-tone object-cover opacity-40 transition-[opacity,transform] duration-[1400ms] ease-expo group-hover:scale-[1.04] group-hover:opacity-60', next.imageClassName)}
                        />

                        <div className='absolute inset-0 bg-gradient-to-r from-background via-background/80 to-background/30' />
                    </div>

                    <Container className='relative flex min-h-[420px] flex-col justify-between gap-12 py-14 md:min-h-[480px] md:py-20'>
                        <div className='flex items-center justify-between gap-4'>
                            <span className='type-label text-muted-foreground'>
                                Next case study
                            </span>

                            <span className='type-label text-ember'>
                                {next.industry}
                            </span>
                        </div>

                        <div className='flex items-end justify-between gap-8'>
                            <div>
                                <p className='type-label text-soft'>
                                    {next.client}
                                </p>

                                <p className='mt-4 max-w-[22ch] font-heading text-[clamp(2rem,4.4vw,4.25rem)] leading-[1.02] tracking-[-0.045em] text-foreground'>
                                    {next.title}
                                </p>
                            </div>

                            <span className='hidden size-16 shrink-0 items-center justify-center bg-foreground text-background transition-transform duration-500 ease-expo group-hover:rotate-45 md:flex'>
                                <ArrowUpRight className='size-6' />
                            </span>
                        </div>
                    </Container>
                </TransitionLink>
            </section>

            <CtaSection
                label='Your project next'
                title={(
                    <>
                        Facing a challenge like {study.client.split(' ')[0]}? <span className='serif-accent'>Let’s talk.</span>
                    </>
                )}
            />
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    return {
        paths: CASE_STUDIES.map((study) => ({ params: { slug: study.slug } })),
        fallback: false
    }
}

export const getStaticProps: GetStaticProps<CaseStudyPageProps> = async ({ params }) => {
    const slug = typeof params?.slug === 'string' ? params.slug : ''
    const study = getCaseStudy(slug)

    if (!study) {
        return { notFound: true }
    }

    const index = CASE_STUDIES.findIndex((item) => item.slug === slug)
    const nextStudy = CASE_STUDIES[(index + 1) % CASE_STUDIES.length]

    return {
        props: {
            study,
            next: {
                slug: nextStudy.slug,
                client: nextStudy.client,
                industry: nextStudy.industry,
                title: nextStudy.title,
                image: nextStudy.image,
                imageAlt: nextStudy.imageAlt,
                ...(nextStudy.imageClassName ? { imageClassName: nextStudy.imageClassName } : {})
            }
        }
    }
}

export default CaseStudyPage
